import { FlowState, Step } from './types'

export function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val))
}

type Action =
  | { type: 'INIT'; steps: Step[]; lives: number; timeLeft: number }
  | { type: 'SYNC_STEPS'; steps: Step[] }
  | { type: 'GO_NEXT' }
  | { type: 'GO_PREV' }
  | { type: 'GO_INDEX'; index: number }
  | { type: 'LOSE_LIFE' }
  | { type: 'SET_TIME_LEFT'; value: number }
  | { type: 'TIME_OVER' }
  | { type: 'COMPLETE' }
  | { type: 'RESET' }
  | { type: 'START_CHALLENGE'; timeLeft: number }
  | { type: 'ENABLE_CONTINUE' }
  | { type: 'VERIFY_RESULT'; correct: boolean; feedback?: string }
  | { type: 'RETRY' }
  | { type: 'CLEAR_FEEDBACK' }

export function flowReducer(state: FlowState, action: Action): FlowState {
  switch (action.type) {
    case 'INIT': {
      const { steps, lives, timeLeft } = action
      return {
        steps,
        currentIndex: 0,
        status: 'active',
        lives,
        maxLives: lives,
        timeLeft,
        canVerify: true,
        canContinue: false,
        phase: 'READY',
        feedback: null
      }
    }

    case 'SYNC_STEPS': {
      const newSteps = action.steps
      if (newSteps.length === 0) {
        return { ...state, steps: newSteps, currentIndex: 0 }
      }
      const cur = state.steps[state.currentIndex]
      let nextIndex = state.currentIndex
      if (cur) {
        const idx = newSteps.findIndex(s => s.id === cur.id)
        if (idx >= 0) nextIndex = idx
      }
      nextIndex = clamp(nextIndex, 0, newSteps.length - 1)
      return {
        ...state,
        steps: newSteps,
        currentIndex: nextIndex
      }
    }

    case 'START_CHALLENGE': {
      return {
        ...state,
        status: 'active',
        timeLeft: Math.max(0, action.timeLeft),
        canVerify: false,
        canContinue: false,
        phase: 'READY',
        feedback: null
      }
    }

    case 'ENABLE_CONTINUE': {
      return { ...state, phase: 'CORRECT', canContinue: true }
    }

    case 'VERIFY_RESULT': {
      if (state.status !== 'active') return state
      if (action.correct) {
        return {
          ...state,
          phase: 'CORRECT',
          canVerify: true,
          canContinue: true,
          feedback: action.feedback
            ? { kind: 'success', message: action.feedback }
            : null
        }
      } else {
        const lives = clamp(state.lives - 1, 0, state.maxLives)
        const status = lives <= 0 ? 'lives_over' : state.status
        return {
          ...state,
          lives,
          status,
          phase: 'LOCKED_WRONG',
          canVerify: false,
          canContinue: false,
          feedback: {
            kind: 'error',
            message: action.feedback ?? 'Tente novamente.'
          }
        }
      }
    }

    case 'RETRY': {
      if (state.status !== 'active') return state
      return {
        ...state,
        phase: 'READY',
        canVerify: false,
        canContinue: false,
        feedback: null
      }
    }

    case 'CLEAR_FEEDBACK':
      return { ...state, feedback: null }

    case 'GO_NEXT': {
      if (state.status !== 'active') return state
      const lastIdx = state.steps.length - 1
      const next = clamp(state.currentIndex + 1, 0, lastIdx)
      if (next >= lastIdx) {
        return { ...state, status: 'completed' }
      }
      return {
        ...state,
        currentIndex: next,
        phase: 'READY',
        feedback: null,
        canVerify: true,
        canContinue: false
      }
    }
    case 'GO_PREV': {
      if (state.status !== 'active') return state
      const prev = clamp(state.currentIndex - 1, 0, state.steps.length - 1)
      return { ...state, currentIndex: prev }
    }
    case 'GO_INDEX': {
      if (state.status !== 'active') return state
      const index = clamp(action.index, 0, state.steps.length - 1)
      return { ...state, currentIndex: index }
    }
    case 'LOSE_LIFE': {
      if (state.status !== 'active') return state
      const lives = clamp(state.lives - 1, 0, state.maxLives)
      const status = lives <= 0 ? 'lives_over' : state.status
      return { ...state, lives, status }
    }
    case 'SET_TIME_LEFT': {
      return { ...state, timeLeft: action.value }
    }
    case 'TIME_OVER':
      return { ...state, status: 'time_over', timeLeft: 0 }
    case 'COMPLETE':
      return { ...state, status: 'completed' }
    case 'RESET':
      return {
        ...state,
        status: 'active',
        currentIndex: 0,
        lives: state.maxLives
      }
    default:
      return state
  }
}
